import { Lock, LockKeyhole, ShieldCheck, HelpCircle, Link2, Megaphone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../configuration';
import { useSelector } from 'react-redux';
import DonateSetting from './tabs/donate-setting';
import ContactSupport from './tabs/contact-support';
import BlockList from './tabs/block-list';
import CustomUrlSetting from './tabs/custom-url-setting';
import CreatorAdsSetting from './tabs/creator-ads-setting';

// Tab mở thẳng được bằng `?tab=`: `customurl` (dải nhắc "chưa có đường dẫn" ở trang cá nhân) và `donate`
// (thông báo hội viên kênh, kèm `muc=hoi-vien` để tab Donate tự chọn mục Hội viên).
const URL_TABS = ['customurl', 'donate', 'quang-cao', 'xac-minh'];
// `xac-minh` mở tab đầu (yêu cầu xác minh creator + eKYC).
const tabFromUrl = (tab: string | null) =>
  tab === 'xac-minh' ? 'tab1' : tab && URL_TABS.includes(tab) ? tab : null;

const UserSetting = () => {
  const [searchParams] = useSearchParams();
  const urlTab = tabFromUrl(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState(urlTab ?? 'tab1');
  // Đang ở trang này mà bấm thông báo dẫn tới tab khác thì chuyển theo.
  useEffect(() => {
    if (urlTab) setActiveTab(urlTab);
  }, [urlTab]);
  const { host, protocol } = window.location;
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const openPopupWindow = () => {
    const url = `${protocol}//${host}/donate-screen/${profile?.userId}?isTest=true`;
    const windowFeatures = 'width=600,height=400,top=100,left=100';
    window.open(url, 'popup', windowFeatures);
  };
  const renderTabDonate = () => {
    return <DonateSetting />;
  };
  return (
    <div className="flex flex-col lg:flex-row justify-evenly gap-4 lg:gap-6 max-w-[1320px] mx-auto mt-2 px-3 lg:px-0">
      {/* Tab selectors */}
      <div className="flex flex-row overflow-x-auto lg:flex-col w-full lg:w-[320px] shrink-0 gap-1 lg:gap-0">
        <div
          className={`shrink-0 cursor-pointer whitespace-nowrap py-4 flex gap-1 font-semibold ${activeTab === 'tab1' ? 'bg-blue-100 rounded-md ' : ''}`}
          onClick={() => setActiveTab('tab1')}
        >
          <div
            className={`border-[1.5px]  rounded-r-[3px] h-6 ${
              activeTab === 'tab1' ? 'border-blue-600' : 'border-transparent'
            } `}
          />
          <ShieldCheck />
          <span className={`px-2 ${activeTab === 'tab1' ? 'text-blue-600' : ''}`}>Yêu cầu xác minh</span>
        </div>
        <div
          className={`shrink-0 cursor-pointer whitespace-nowrap py-4 flex gap-1 font-semibold ${activeTab === 'tab2' ? 'bg-blue-100 rounded-md ' : ''}`}
          onClick={() => setActiveTab('tab2')}
        >
          <div
            className={`border-[1.5px]  rounded-r-[3px] h-6 ${
              activeTab === 'tab2' ? 'border-blue-600' : 'border-transparent'
            } `}
          />
          <LockKeyhole />
          <span className={`px-2 ${activeTab === 'tab2' ? 'text-blue-600' : ''}`}>Bảo mật</span>
        </div>
        <div
          className={`shrink-0 cursor-pointer whitespace-nowrap py-4 flex gap-1 font-semibold ${activeTab === 'customurl' ? 'bg-blue-100 rounded-md ' : ''}`}
          onClick={() => setActiveTab('customurl')}
        >
          <div
            className={`border-[1.5px]  rounded-r-[3px] h-6 ${
              activeTab === 'customurl' ? 'border-blue-600' : 'border-transparent'
            } `}
          />
          <Link2 />
          <span className={`px-2 ${activeTab === 'customurl' ? 'text-blue-600' : ''}`}>Đường dẫn</span>
        </div>
        <div
          className={`shrink-0 cursor-pointer whitespace-nowrap py-4 flex gap-1 font-semibold ${activeTab === 'donate' ? 'bg-blue-100 rounded-md ' : ''}`}
          onClick={() => setActiveTab('donate')}
        >
          <div
            className={`border-[1.5px]  rounded-r-[3px] h-6 ${
              activeTab === 'donate' ? 'border-blue-600' : 'border-transparent'
            } `}
          />
          <LockKeyhole />
          <span className={`px-2 ${activeTab === 'donate' ? 'text-blue-600' : ''}`}>Donate</span>
        </div>
        <div
          className={`shrink-0 cursor-pointer whitespace-nowrap py-4 flex gap-1 font-semibold ${activeTab === 'quang-cao' ? 'bg-blue-100 rounded-md ' : ''}`}
          onClick={() => setActiveTab('quang-cao')}
        >
          <div
            className={`border-[1.5px]  rounded-r-[3px] h-6 ${
              activeTab === 'quang-cao' ? 'border-blue-600' : 'border-transparent'
            } `}
          />
          <Megaphone />
          <span className={`px-2 ${activeTab === 'quang-cao' ? 'text-blue-600' : ''}`}>Kiếm tiền quảng cáo</span>
        </div>
      </div>

      {/* Content area */}
      <div className="rounded-md w-full lg:w-[894px] min-w-0 h-fit bg-none p-2">
        {activeTab === 'tab1' && (
          <div className="bg-white dark:bg-dark2 shadow-lg dark:shadow-none p-4 rounded-md">
            <span className="font-bold text-lg">Yêu cầu xác minh cho Content Creator</span>
            {/* Trạng thái chờ duyệt / bị từ chối nay hiện trong ContactSupport theo danh sách điều kiện máy chủ trả. */}
            {profile?.userType === 'CONTENT_CREATOR' ? (
              <p className="mt-2 text-base text-green-600">
                Tài khoản của bạn đã có tích xanh creator.
              </p>
            ) : (
              <ContactSupport />
            )}
          </div>
        )}
        {activeTab === 'tab2' && <BlockList />}
        {activeTab === 'customurl' && <CustomUrlSetting />}
        {activeTab === 'donate' && renderTabDonate()}
        {activeTab === 'quang-cao' && <CreatorAdsSetting />}
      </div>
    </div>
  );
};

export default UserSetting;
